<template>
  <v-dialog v-model="open" max-width="500px">
    <v-card :loading="loading">
      <v-card-title>
        <span class="headline">
          {{
            editedInventory.id
              ? $localizationService.localize(
                  "inventory_page.dialog.add_and_edit.edit_title"
                )
              : $localizationService.localize(
                  "inventory_page.dialog.add_and_edit.new_title"
                )
          }}
        </span>
      </v-card-title>

      <v-card-text>
        <v-container v-if="open">
          <v-row>
            <v-text-field
              v-model="editedInventory.itemName"
              :label="
                $localizationService.localize(
                  'inventory_page.dialog.add_and_edit.param.item_name'
                ) + '*'
              "
              :rules="itemNameRules"
              :disabled="loading"
              counter="255"
              maxlength="255"
            ></v-text-field>
          </v-row>
          <v-row>
            <v-select
              v-model="editedInventory.size"
              :label="
                $localizationService.localize(
                  'inventory_page.dialog.add_and_edit.param.size'
                ) + '*'
              "
              :items="sizeOptions"
              item-text="text"
              item-value="value"
              :disabled="loading"
              :rules="sizeRules"
              :menu-props="{ offsetY: true }"
            ></v-select>
          </v-row>
          <v-row>
            <v-text-field
              v-model.number="editedInventory.totalQty"
              :label="
                $localizationService.localize(
                  'inventory_page.dialog.add_and_edit.param.total_qty'
                ) + '*'
              "
              :rules="totalQtyRules"
              :disabled="loading"
              type="number"
              min="0"
            ></v-text-field>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="open = false" :disabled="loading">
          {{ $localizationService.localize("btn.cancel") }}
        </v-btn>
        <v-btn
          color="primary"
          text
          @click="saveInventory"
          :disabled="!saveButtonActive"
        >
          {{
            editedInventory.id
              ? $localizationService.localize("btn.save")
              : $localizationService.localize("btn.add")
          }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Vue from "vue";
import { ITEM_SIZE_OPTIONS } from "@/types/types";

export default {
  name: "EditInventoryDialog",

  data: () => ({
    editedInventoryOriginal: {},
    editedInventory: {},

    open: false,
    loading: false,

    sizeOptions: ITEM_SIZE_OPTIONS,

    itemNameRules: [
      // eslint-disable-next-line no-mixed-operators
      (value) => !!value && value.trim().length > 0 || "Required",
      (value) => !value || value.length <= 255 || "Item name cannot exceed 255 characters",
    ],

    sizeRules: [
      // eslint-disable-next-line no-mixed-operators
      (value) => !!value || "Required",
    ],

    totalQtyRules: [
      // eslint-disable-next-line no-mixed-operators
      (value) => value !== null && value !== undefined && value !== "" || "Required",
      (value) => Number.isInteger(Number(value)) || "Must be a whole number",
      (value) => Number(value) >= 0 || "Cannot be negative",
    ],
  }),

  computed: {
    saveButtonActive() {
      if (this.loading) return false;

      if (
        this.itemNameRules.find(
          (rule) => rule(this.editedInventory.itemName) !== true,
        )
      ) {
        return false;
      }

      if (
        this.sizeRules.find(
          (rule) => rule(this.editedInventory.size) !== true,
        )
      ) {
        return false;
      }

      if (
        this.totalQtyRules.find(
          (rule) => rule(this.editedInventory.totalQty) !== true,
        )
      ) {
        return false;
      }

      return Object.keys(this.editedInventory).find(
        (key) => this.editedInventoryOriginal[key] != this.editedInventory[key],
      );
    },
  },

  methods: {
    openDialog(inventory) {
      this.editedInventoryOriginal = inventory ? { ...inventory } : {};
      this.editedInventory = inventory ? { ...inventory } : { totalQty: 0 };
      this.open = true;
    },

    async saveInventory() {
      this.loading = true;
      try {
        const result = await this.$store.dispatch(
          "inventoryModule/addOrSaveInventory",
          {
            id: this.editedInventory.id,
            itemName: this.editedInventory.itemName.trim(),
            size: this.editedInventory.size || null,
            totalQty: Number(this.editedInventory.totalQty),
          },
        );
        Vue.toasted.success(result, {
          duration: 5000,
        });
        this.open = false;
      } catch (e) {
        Vue.toasted.error(e, {
          duration: 5000,
        });
      }
      this.loading = false;
    },
  },
};
</script>

<style scoped>
/* Hide spin buttons for number input */
::v-deep input[type="number"]::-webkit-outer-spin-button,
::v-deep input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

::v-deep input[type="number"] {
  -moz-appearance: textfield;
}
</style>
